import LeftArrow from '@repo/ui/src/components/Icons/LeftArrow';
import RightArrow from '@repo/ui/src/components/Icons/RightArrow';
import { MediaType } from '@repo/ui/src/types/image';
import React from 'react';
import useTranslation from '@components/Translation/useTranslation';
import {
  CarouselProvider,
  Slider,
  Slide,
  Dot,
  CarouselContext,
  ButtonBack,
  ButtonNext,
  CarouselStoreInterface,
} from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import ZoomIcon from '@repo/ui/src/components/Icons/ZoomIcon';
import Image from '@repo/ui/src/components/Image/New';
import { classNames } from '@repo/ui/src/utils/classNames';

interface CarouselStateProps {
  onChange: (context: CarouselStoreInterface) => void;
}

const CarouselState = ({ onChange }: CarouselStateProps) => {
  const context = React.useContext(CarouselContext);
  React.useEffect(() => {
    const handleChange = () => onChange(context);
    context.subscribe(handleChange);
    return () => context.unsubscribe(handleChange);
  }, [onChange]);
  return null;
};

interface PDPHeroCarouselProps {
  dark?: boolean;
  images: Array<MediaType>;
  openGalleryDialog: (images: Array<MediaType>, open: boolean) => void;
}

const PDPHeroCarousel = ({ dark, images = [], openGalleryDialog }: PDPHeroCarouselProps) => {
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const onChange = React.useCallback(
    (context: CarouselStoreInterface) => setCurrentImageIndex(context.state.currentSlide),
    [],
  );

  return (
    <div className="tw-w-full tw-h-full tw-px-9 tw-grid md:tw-justify-stretch md:tw-content-center">
      <h2 className="sr-only">
        {t('productDetailPageContent.imageGalleryText', undefined, { default: 'Image Gallery' })}:{' '}
        {images.length} {t('genericContent.imagesText', undefined, { default: 'Images' })}
      </h2>
      <CarouselProvider
        naturalSlideWidth={500}
        naturalSlideHeight={500}
        totalSlides={images.length}
        className="tw-w-full tw-h-full tw-grid tw-gap-6 tw-grid-cols-1 tw-max-h-[70vh]"
        infinite
      >
        <CarouselState onChange={onChange} />
        <Slider>
          {images.map((image, i) => (
            <Slide
              key={i}
              index={i}
              innerClassName="tw-grid tw-place-content-center tw-max-h-[calc(70vh_-_108px)]"
            >
              <Image
                src={buildImageSrc(image, 800)}
                alt={`Slide ${i + 1} of ${images.length}`}
                className="tw-w-full tw-object-contain grill-image tw-max-h-[calc(70vh_-_108px)] tw-aspect-square"
                height={600}
                width={600}
                priority
              />
            </Slide>
          ))}
        </Slider>
        <div className="tw-grid tw-gap-6 tw-grid-cols-1">
          <div className="tw-grid tw-grid-flow-col tw-justify-center tw-gap-4 tw-items-center">
            <ButtonBack>
              <LeftArrow
                aria-label={t('genericContent.previousButtonLabel', undefined, {
                  default: 'Previous',
                })}
                className={classNames(
                  'tw-block focus-visible:tw-outline-2 focus-visible:tw-outline-action-400 hover:tw-text-action-200',
                  dark
                    ? 'tw-text-surface-400 focus:tw-text-white hover:tw-text-white'
                    : 'tw-text-surface-500 focus:tw-text-surface-500 hover:tw-text-surface-500',
                )}
              />
            </ButtonBack>
            <ButtonNext>
              <RightArrow
                aria-label={t('genericContent.nextButtonLabel', undefined, { default: 'Next' })}
                className={classNames(
                  'tw-block focus-visible:tw-outline-2 focus-visible:tw-outline-action-400 hover:tw-text-action-200',
                  dark
                    ? 'tw-text-surface-400 focus:tw-text-white hover:tw-text-white'
                    : 'tw-text-surface-500 focus:tw-text-surface-500 hover:tw-text-surface-500',
                )}
              />
            </ButtonNext>
            <button
              className={classNames(
                'hover:tw-text-action-200',
                dark
                  ? 'tw-text-surface-400 focus:tw-text-white hover:tw-text-white'
                  : 'tw-text-surface-500 focus:tw-text-surface-500 hover:tw-text-surface-500',
              )}
              aria-label={t('productDetailPageContent.zoomButtonLabel', undefined, {
                default: 'Open Image Gallery Dialog',
              })}
              onClick={() =>
                openGalleryDialog(
                  [...images.slice(currentImageIndex), ...images.slice(0, currentImageIndex)],
                  true,
                )
              }
            >
              <ZoomIcon
                className="tw-block"
                aria-label={t('productDetailPageContent.zoomButtonLabel', undefined, {
                  default: 'Open Image Gallery Dialog',
                })}
              />
            </button>
          </div>
          <div className="tw-flex z-index-30 tw-list-none tw-justify-center tw-text-[0] tw-py-2 tw-gap-2">
            {images.map((_, i) => (
              <Dot
                key={i}
                slide={i}
                aria-label={`Go to Slide ${i + 1}`}
                aria-current={currentImageIndex === i}
                className={classNames(
                  'tw-w-12 tw-flex-shrink tw-relative tw-py-3 after:tw-absolute after:tw-block after:tw-inset-x-0 after:tw-top-2.5 after:tw-h-1 after:tw-transition-colors after:tw-duration-300 after:tw-transition-ease-out focus-visible:tw-outline-1 focus-visible:tw-outline-surface-white',
                  dark ? 'hover:after:tw-bg-white' : 'hover:after:tw-bg-surface-500',
                  currentImageIndex === i ? 'after:tw-bg-action-400' : 'after:tw-bg-surface-400',
                )}
              >
                {i + 1}
              </Dot>
            ))}
          </div>
        </div>
      </CarouselProvider>
    </div>
  );
};
export default PDPHeroCarousel;
